export type ThemeMode = "light" | "dark";
export type ThemeColorSurface = "default" | "chat";

export const THEME_STORAGE_KEY = "yara-theme";
const DARK_MODE_QUERY = "(prefers-color-scheme: dark)";
const THEME_COLORS: Record<ThemeMode, Record<ThemeColorSurface, string>> = {
  light: {
    default: "#FFF8EE",
    chat: "#FBF1EA",
  },
  dark: {
    default: "#171124",
    chat: "#151B21",
  },
};
const themeListeners = new Set<(theme: ThemeMode) => void>();
let activeThemeColorSurface: ThemeColorSurface = "default";
let stopSystemThemeSync: (() => void) | null = null;

export function isThemeMode(value: string | null | undefined): value is ThemeMode {
  return value === "light" || value === "dark";
}

export function resolveThemePreference(storedTheme: string | null | undefined, systemPrefersDark: boolean): ThemeMode {
  if (isThemeMode(storedTheme)) return storedTheme;
  return systemPrefersDark ? "dark" : "light";
}

function getStoredTheme(): ThemeMode | null {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeMode(value) ? value : null;
  } catch {
    return null;
  }
}

function systemPrefersDark(): boolean {
  return !!window.matchMedia?.(DARK_MODE_QUERY).matches;
}

function applyThemeColorMeta(theme: ThemeMode, surface: ThemeColorSurface = "default") {
  let themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');

  if (!themeColor) {
    themeColor = document.createElement("meta");
    themeColor.name = "theme-color";
    document.head.appendChild(themeColor);
  }

  themeColor.content = THEME_COLORS[theme][surface];
  themeColor.removeAttribute("media");
}

export function applyThemeColorSurface(surface: ThemeColorSurface = "default"): ThemeMode {
  activeThemeColorSurface = surface;
  const current = document.documentElement.dataset.theme;
  const theme = isThemeMode(current) ? current : resolveThemePreference(getStoredTheme(), systemPrefersDark());
  applyThemeColorMeta(theme, surface);
  return theme;
}

export function applyTheme(theme: ThemeMode): ThemeMode {
  const changed = document.documentElement.dataset.theme !== theme;
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
  applyThemeColorMeta(theme, activeThemeColorSurface);
  if (changed) {
    themeListeners.forEach((listener) => listener(theme));
  }
  return theme;
}

export function initializeTheme(): ThemeMode {
  stopSystemThemeSync?.();
  stopSystemThemeSync = null;
  activeThemeColorSurface = "default";

  const media = window.matchMedia?.(DARK_MODE_QUERY);
  const theme = applyTheme(resolveThemePreference(getStoredTheme(), !!media?.matches));
  if (!media) return theme;

  const syncWithSystem = (event: MediaQueryListEvent) => {
    if (getStoredTheme()) return;
    applyTheme(event.matches ? "dark" : "light");
  };

  if (typeof media.addEventListener === "function") {
    media.addEventListener("change", syncWithSystem);
    stopSystemThemeSync = () => media.removeEventListener("change", syncWithSystem);
  } else if (typeof media.addListener === "function") {
    media.addListener(syncWithSystem);
    stopSystemThemeSync = () => media.removeListener(syncWithSystem);
  }

  return theme;
}

export function getAppliedTheme(): ThemeMode {
  const current = document.documentElement.dataset.theme;
  return isThemeMode(current) ? current : initializeTheme();
}

export function setThemePreference(theme: ThemeMode): ThemeMode {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Theme still applies for the current session when storage is unavailable.
  }
  return applyTheme(theme);
}

export function subscribeToThemeChanges(listener: (theme: ThemeMode) => void) {
  themeListeners.add(listener);
  return () => {
    themeListeners.delete(listener);
  };
}
